import { useTheme } from '@emotion/react';
import { useEffect, useState } from 'react';
import { fetchViewed } from '../../../utils/viewed';
import { http } from '../../../lib/api/http';
import { V2, V4 } from '../../../lib/api/urls';
import { DEVICE_SCREENS_MAX_STRING } from '../../../lib/consts';
import { BreadCrumbs } from '../../blocks/BreadCrumbs';
import { H1 } from '../../styled/typography';
import { CompilationSlider } from '../../compilation-slider/compilation-slider';
import { Container } from '../../layout/Container';
import { ShareBlock } from '../../blocks/ShareBlock';
import { ActionCardColumn } from '../../actions/ActionCardColumn';
import { Compilations } from '../../compilations/compilations';
import { Viewed } from '../../viewed/viewed';
import { Subscription } from '../../about/Subscription';
import { MobileApps } from '../../about/MobileApps';
import { UpButton } from '../../common/UpButton';
import ViewSVG from '../../../public/img/views2.svg';
import { CropText } from '../../collapsable/collapsable';
import { ButtonShowMore } from '../../Buttons';
import { StandardButton } from '../../styled/Button';
import { activeStatus } from '../../../constants/events';
import { COMPILATION_EVENTS_COUNT } from '../../../utils/consts';
import { Action, City, Compilation } from '../../../models';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../../../lib/api/browserStorage';

export function CompilationPage({
  currentCity,
  compilation,
  initialEventsList,
  compilationsList,
}: {
  currentCity: City;
  compilation: Compilation;
  compilationsList: { results: Compilation[]; next: string | null };
  initialEventsList: { results: Action[]; next: string | null };
}) {
  const theme = useTheme();
  const [viewed, setViewed] = useState<{ results: Action[] }>({ results: [] });
  const [eventsList, setEventsList] = useState<{
    results: Action[];
    next: string | null;
  } | null>(null);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
    setEventsList(initialEventsList);
  }, [compilation.id]);

  const showMoreEvents = async () => {
    if (eventsList) {
      setIsLoadingEvents(true);
      const newEventsList = await http.get(
        V4.events,
        {
          ids: compilation.events,
          status: activeStatus,
          is_web: 'true',
          limit: COMPILATION_EVENTS_COUNT,
          offset: page * COMPILATION_EVENTS_COUNT,
        },
        null,
        { arrayFormat: 'comma' }
      );
      setEventsList({
        ...newEventsList,
        results: eventsList.results.concat(newEventsList.results),
      });
      setPage(page + 1);
      setIsLoadingEvents(false);
    }
  };

  useEffect(() => {
    http.post(V2.eventViews, {
      Compilation: compilation.remote_id,
    });
  }, [compilation.id]);
  useEffect(() => {
    async function fetch() {
      const viewedList = await fetchViewed();
      setViewed(viewedList);
    }
    fetch();
    let viewedActions = [];
    viewedActions =
      (JSON.parse(getLocalStorageItem('viewed-events') || '[]') || []).slice(
        0,
        8
      ) || [];
    setLocalStorageItem('viewed-events', JSON.stringify([...viewedActions]));
  }, [compilation.id]);

  return (
    <Container
      css={{
        padding: '16px 12px',
        [DEVICE_SCREENS_MAX_STRING.md]: {
          padding: '28px 8px 0 8px',
        },
      }}
    >
      <div
        css={{
          '> div': {
            [DEVICE_SCREENS_MAX_STRING.md]: {
              display: 'inline-flex',
            },
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              display: 'none',
            },
          },
        }}
      >
        <BreadCrumbs
          currentCity={currentCity}
          items={[
            {
              title: 'Подборки',
              url: '/compilations/',
            },
            { title: compilation.title },
          ]}
        />
        <H1
          css={{
            width: '100%',
            textAlign: 'center',
            fontFamily: 'Montserrat',
            lineHeight: '38px',
            letterSpacing: '0.01em',
            color: theme.colors.text,
            fontWeight: 700,
            fontSize: 32,
            marginTop: 16,
            marginBottom: '4rem',
            [DEVICE_SCREENS_MAX_STRING.md]: {
              textAlign: 'left',
              marginTop: '1.5rem',
              marginBottom: '2rem',
              fontSize: 32,
            },
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              margin: '0.75em 0 0.75em',
              fontSize: 22,
              lineHeight: '26px',
            },
          }}
        >
          {compilation.title}
        </H1>
      </div>
      {compilation.images.length > 0 &&
        !(compilation.images.length === 1 && compilation.images[0].is_main) && (
          <CompilationSlider
            images={compilation.images}
            compilation={compilation}
          />
        )}
      <div
        css={{
          fontFamily: 'Golos',
          fontSize: 24,
          lineHeight: '150%',
          textAlign: 'center',
          padding: '0 10%',
          color: '#000000',
          display: 'block',
          fontWeight: 300,
          marginBottom: '2rem',
          [DEVICE_SCREENS_MAX_STRING.xl]: {
            fontSize: 20,
          },
          [DEVICE_SCREENS_MAX_STRING.lg]: {
            fontWeight: 800,
          },
          [DEVICE_SCREENS_MAX_STRING.md]: {
            fontSize: 16,
            lineHeight: '140%',
            padding: '0 7%',
          },
          [DEVICE_SCREENS_MAX_STRING.sm]: {
            fontSize: 13,
            lineHeight: '140%',
            padding: '0 1%',
            fontWeight: 600,
          },
        }}
      >
        {compilation.lead_text}
      </div>
      {!!compilation.views && (
        <div
          css={{
            fontFamily: 'Golos',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            margin: '20px 10%',
            color: '#888',
            fontWeight: 500,
          }}
        >
          <ViewSVG css={{ width: 20, height: 18, fill: '#BFC1C3' }} />{' '}
          {compilation.views}
        </div>
      )}
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-end',
          marginRight: '10%',
          marginBottom: 15,
          textAlign: 'right',
          span: {
            marginRight: 5,
          },
        }}
      >
        <ShareBlock item={compilation} />
      </div>
      <main css={{ fontFamily: 'Golos', color: theme.colors.black }}>
        <div
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          css={{
            marginBottom: '4rem',
            ...theme.mixins.row(),
            [DEVICE_SCREENS_MAX_STRING.md]: {
              marginBottom: '2rem',
            },
          }}
        >
          <div
            // actions-column
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            css={{
              ...theme.mixins.flex(),
            }}
          >
            {eventsList
              ? eventsList.results.map((item) => (
                  <ActionCardColumn
                    key={`actionCardHorizontal${item.id}`}
                    item={item}
                  />
                ))
              : initialEventsList.results.map((item) => (
                  <ActionCardColumn
                    key={`actionCardHorizontal${item.id}`}
                    item={item}
                  />
                ))}
          </div>
          {eventsList && eventsList.next && (
            <div
              css={{
                width: '100%',
                display: 'flex',
                gap: 10,
                padding: '0 8px',
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  paddingTop: 10,
                },
              }}
            >
              <ButtonShowMore
                callback={showMoreEvents}
                loadingText={isLoadingEvents ? 'Загрузка...' : ''}
              />
              <StandardButton callback={() => window.scrollTo(0, 0)}>
                Наверх
              </StandardButton>
            </div>
          )}
        </div>
        <Compilations
          title='Другие подборки'
          compilations={compilationsList}
          currentCity={currentCity}
          href={['', currentCity.url, 'compilations', ''].join('/')}
        />
        <Viewed title='Вы смотрели' viewed={viewed} />
        {compilation.description && (
          <div
            css={{
              marginBottom: 60,
              [DEVICE_SCREENS_MAX_STRING.xl]: {
                paddingTop: '2rem',
              },
            }}
          >
            <CropText
              text={compilation.description}
              deskTopCount={1000}
              mobileCount={1000}
            />
          </div>
        )}
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: '4rem',
            [DEVICE_SCREENS_MAX_STRING.xl]: {
              gridTemplateColumns: '1fr',
              marginBottom: '2rem',
            },
          }}
        >
          <Subscription currentCity={currentCity} />
          <MobileApps />
        </div>
        <UpButton screenCount={1.5} />
      </main>
    </Container>
  );
}
